import React, { useState, useEffect } from 'react'
import { useTimer } from '../../contexts/TimerContext'
import TimerDisplay from '../../components/Timer/TimerDisplay'
import TimerOperationCard from '../../components/Timer/TimerOperationCard'
import TimeEntryList from '../../components/Timer/TimeEntryList'
import ManualTimeEntryModal, { ManualTimeEntryData } from '../../components/Timer/ManualTimeEntryModal'
import EditTimeEntryModal, { EditTimeEntryData } from '../../components/Timer/EditTimeEntryModal'
import WeekLockStatusBanner from '../../components/Timer/WeekLockStatusBanner'
import { TimeEntry, ViewMode, WeeklyEntries } from '../../types'
import { timerService } from '../../services/timerService'
import { AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

const TimerPage: React.FC = () => {
  const [isManualModalOpen, setIsManualModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  
  // 檢視模式狀態
  const [viewMode, setViewMode] = useState<ViewMode>('today')
  const [weeklyEntries, setWeeklyEntries] = useState<WeeklyEntries>({})
  const [weeklyTotalDuration, setWeeklyTotalDuration] = useState(0)

  const {
    // 計時器狀態
    isRunning,
    elapsedTime,
    
    // 專案相關
    projects,
    selectedProject,
    recentFavoriteProjects,
    selectProject,
    
    // 工作描述
    description,
    setDescription,
    
    // 今日記錄
    todayEntries,
    totalTodayDuration,
    
    // 週次鎖定狀態
    weekLockStatus,
    isCheckingWeekLock,
    
    // 載入狀態
    isLoading,
    error,
    
    // 動作函數
    startTimer,
    stopTimer,
    deleteTimeEntry,
    createManualTimeEntry,
    editTimeEntry,
    checkCurrentWeekLock,
    loadProjects
  } = useTimer()

  // 處理開始計時
  const handleStartTimer = async () => {
    try {
      await startTimer()
      toast.success('計時已開始')
    } catch (error) {
      toast.error('開始計時失敗')
    }
  }

  // 處理停止計時
  const handleStopTimer = async () => {
    try {
      await stopTimer()
      toast.success('計時已停止')
    } catch (error) {
      toast.error('停止計時失敗')
    }
  }

  // 處理編輯計時記錄
  const handleEditEntry = (entry: TimeEntry, originalDate?: string) => {
    // 不允許編輯進行中的記錄
    if (entry.status === 'RUNNING') {
      toast.error('無法編輯進行中的計時記錄')
      return
    }
    
    // 儲存原始日期信息以便後續使用
    setEditingEntry({
      ...entry,
      originalDate
    } as TimeEntry & { originalDate?: string })
    setIsEditModalOpen(true)
  }

  // 處理編輯提交
  const handleEditSubmit = async (data: EditTimeEntryData) => {
    try {
      // 傳遞 originalDate 給編輯函數
      const editData = {
        ...data,
        originalDate: (editingEntry as TimeEntry & { originalDate?: string })?.originalDate
      }
      
      await editTimeEntry(editData)
      setIsEditModalOpen(false)
      setEditingEntry(null)
      
      // 如果是週檢視模式，刷新週檢視數據
      if (viewMode === 'week') {
        await loadWeeklyEntries()
      }
      
      // toast.success 在 Modal 中已經處理
    } catch (error) {
      // toast.error 在 Modal 中已經處理
    }
  }

  // 處理編輯取消
  const handleEditCancel = () => {
    setIsEditModalOpen(false)
    setEditingEntry(null)
  }

  // 處理刪除計時記錄
  const handleDeleteEntry = async (entryId: string) => {
    if (window.confirm('確定要刪除這筆計時記錄嗎？')) {
      try {
        await deleteTimeEntry(entryId)
        toast.success('記錄已刪除')
      } catch (error) {
        toast.error('刪除記錄失敗')
      }
    }
  }

  // 處理手動新增時間記錄
  const handleManualAdd = () => {
    setIsManualModalOpen(true)
  }

  // 處理手動新增提交
  const handleManualSubmit = async (data: ManualTimeEntryData) => {
    try {
      await createManualTimeEntry(data)
      setIsManualModalOpen(false)
      // toast.success 在 Modal 中已經處理
    } catch (error) {
      // toast.error 在 Modal 中已經處理
    }
  }

  // 處理手動新增取消
  const handleManualCancel = () => {
    setIsManualModalOpen(false)
  }

  // 處理重新載入專案
  const handleReloadProjects = async () => {
    try {
      await loadProjects(true) // 強制重新載入專案
      toast.success('專案列表已更新')
    } catch (error) {
      toast.error('重新載入專案失敗')
    }
  }

  // 載入週檢視資料
  const loadWeeklyEntries = async () => {
    if (viewMode !== 'week') return
    
    try {
      const data = await timerService.getWeeklyEntries()
      setWeeklyEntries(data.weeklyEntries)
      setWeeklyTotalDuration(data.totalDuration)
    } catch (error) {
      toast.error('載入週記錄失敗')
      console.error('Failed to load weekly entries:', error)
    }
  }

  // 處理檢視模式變更
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
  }

  // 當檢視模式變更時載入對應資料
  useEffect(() => {
    if (viewMode === 'week') {
      loadWeeklyEntries()
    }
  }, [viewMode])

  // 檢查是否可以進行操作（根據週次鎖定狀態）  
  const canOperate = !weekLockStatus?.isLocked

  return (
    <div className="space-y-3 md:space-y-6 animate-fade-in">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between p-3 md:p-6 bg-gradient-to-r from-white to-primary-50/30 rounded-lg md:rounded-xl shadow-sm border border-primary-100/50">
        <div className="flex items-center space-x-2 md:space-x-4">
          <div className="w-1.5 h-6 md:w-2 md:h-8 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full"></div>
          <h1 className="text-xl md:text-3xl font-bold text-secondary-800">計時器</h1>
        </div>
        
        {/* 今日總計 */}
        <div className="text-right bg-white/60 backdrop-blur-sm rounded-md md:rounded-lg p-2 md:p-4 shadow-sm">
          <div className="text-xs md:text-sm text-secondary-600 font-medium">今日總計</div>
          <div className="text-lg md:text-2xl font-bold text-primary-600 font-mono">
            {Math.floor(totalTodayDuration / 3600)}h {Math.floor((totalTodayDuration % 3600) / 60)}m
          </div>
        </div>
      </div>

      {/* 錯誤提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* 週次鎖定狀態橫幅 */}
      {weekLockStatus && (
        <WeekLockStatusBanner 
          lockStatus={weekLockStatus}
          onRefresh={checkCurrentWeekLock}
          isRefreshing={isCheckingWeekLock}
        />
      )}

      {/* 手機版：單欄佈局 */}
      <div className="block lg:hidden space-y-3">
        {/* 計時器顯示 */}
        <div className="card p-3">
          <TimerDisplay
            elapsedTime={elapsedTime}
            isRunning={isRunning}
          />
        </div>

        {/* 操作卡片 */}
        <TimerOperationCard
          isRunning={isRunning}
          isLoading={isLoading}
          description={description}
          onDescriptionChange={setDescription}
          projects={projects}
          selectedProject={selectedProject}
          recentFavoriteProjects={recentFavoriteProjects}
          onSelectProject={selectProject}
          onReloadProjects={handleReloadProjects}
          onStart={handleStartTimer}
          onStop={handleStopTimer}
          disabled={!canOperate}
        />

        {/* 記錄檢視 */}
        <div className="card p-3 md:p-4">
          <TimeEntryList
            entries={todayEntries}
            totalDuration={totalTodayDuration}
            weeklyEntries={weeklyEntries}
            weeklyTotalDuration={weeklyTotalDuration}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
            onStop={handleStopTimer}
            onManualAdd={handleManualAdd}
            disabled={!canOperate}
          />
        </div>
      </div>

      {/* 桌機版：雙欄佈局 */}
      <div className="hidden lg:grid grid-cols-2 gap-6">
        {/* 左側：計時器控制 */}
        <div className="space-y-6">
          {/* 計時器顯示 - 純狀態顯示 */}
          <div className="card p-6">
            <TimerDisplay
              elapsedTime={elapsedTime}
              isRunning={isRunning}
            />
          </div>

          {/* 操作卡片 - 整合所有操作功能 */}
          <TimerOperationCard
            isRunning={isRunning}
            isLoading={isLoading}
            description={description}
            onDescriptionChange={setDescription}
            projects={projects}
            selectedProject={selectedProject}
            recentFavoriteProjects={recentFavoriteProjects}
            onSelectProject={selectProject}
            onReloadProjects={handleReloadProjects}
            onStart={handleStartTimer}
            onStop={handleStopTimer}
            disabled={!canOperate}
          />
        </div>

        {/* 右側：記錄檢視 */}
        <div className="card p-6">
          <TimeEntryList
            entries={todayEntries}
            totalDuration={totalTodayDuration}
            weeklyEntries={weeklyEntries}
            weeklyTotalDuration={weeklyTotalDuration}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
            onStop={handleStopTimer}
            onManualAdd={handleManualAdd}
            disabled={!canOperate}
          />
        </div>
      </div>

      {/* 快捷鍵提示 */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-2 md:p-4">
        <div className="text-xs md:text-sm text-primary-800">
          <strong>快捷鍵：</strong>
          <span className="ml-1 md:ml-2">空白鍵 - 開始/停止計時</span>
          <span className="ml-2 md:ml-4 hidden sm:inline">Esc - 清空描述</span>
        </div>
      </div>

      {/* 手動新增時間記錄 Modal */}
      <ManualTimeEntryModal
        isOpen={isManualModalOpen}
        onClose={handleManualCancel}
        onSubmit={handleManualSubmit}
        projects={projects}
        isLoading={isLoading}
      />

      {/* 編輯時間記錄 Modal */}
      <EditTimeEntryModal
        isOpen={isEditModalOpen}
        entry={editingEntry}
        onClose={handleEditCancel}
        onSubmit={handleEditSubmit}
        projects={projects}
        isLoading={isLoading}
      />
    </div>
  )
}

export default TimerPage
